import { Suspense, useEffect, useRef } from "react";
import { useFetcher } from "react-router";

import type { BuilderContent } from "@builder.io/sdk-react";

import { ModelMap } from "~/hec/builder/model-map";
import { PrefetchedBuilderContent } from "~/hec/components/prefetched-builder-content";
import type { loader } from "~/routes/resources/plp-bottom-banner";

const RESOURCE_URL = "/resources/plp-bottom-banner";

// How far below the viewport the slot may be when the fetch fires. Generous on
// purpose: the content usually lands before the shopper reaches the slot, so
// the late insert (which grows the page under the SEO copy + footer) happens
// off-screen instead of visibly pushing content around.
const NEAR_VIEWPORT_ROOT_MARGIN = "800px 0px";

/**
 * PLP Bottom Banner — the Top Banner's below-the-grid counterpart (rendered
 * under the Load More button, above the SEO copy block). Unlike the Top
 * Banner it is NOT fetched by the PLP loader: the slot is below the fold on
 * every device, so the content is scroll-lazy — an IntersectionObserver
 * sentinel `fetcher.load`s `/resources/plp-bottom-banner` (server-side
 * `swrFetchOneEntry`, same category targeting + scheduling semantics as the
 * Top Banner) once the shopper scrolls within range. Categories with no
 * targeted entry render nothing and reserve no space.
 *
 * `previewContent` (the `/edit/plp-bottom-banner/:id` route) bypasses the
 * lazy gate entirely: an author's editor iframe must render the section
 * immediately — and keep the Builder `<Content>` mounted for live edit
 * updates — rather than wait on a scroll that the height-capped preview grid
 * would never produce.
 */
export function PlpBottomBannerSection({
  categoryId,
  previewContent,
}: {
  categoryId: string | null | undefined;
  /** Editor-preview content; `undefined` = normal scroll-lazy behavior. */
  previewContent?: BuilderContent | null;
}) {
  const isPreview = previewContent !== undefined;

  const fetcher = useFetcher<typeof loader>({
    key: `plp-bottom-banner-${categoryId ?? "none"}`,
  });
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Guards one load per category (soft-nav to another PLP re-arms via the
  // effect deps; the observer is disconnected on trigger and on cleanup).
  const loadedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (isPreview || !categoryId) return;
    if (loadedForRef.current === categoryId) return;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        if (loadedForRef.current === categoryId) return;
        loadedForRef.current = categoryId;
        fetcher.load(
          `${RESOURCE_URL}?categoryId=${encodeURIComponent(categoryId)}`,
        );
        observer.disconnect();
      },
      { rootMargin: NEAR_VIEWPORT_ROOT_MARGIN },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // `fetcher` is intentionally EXCLUDED: useFetcher returns a new object
    // every render, so including it re-runs the effect (and re-creates the
    // observer) on every render. `fetcher.load` is keyed by the stable fetcher
    // key, so the captured instance is safe to call (see use-fetch-lms.ts).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview, categoryId]);

  if (!isPreview && !categoryId) return null;

  const content = isPreview ? previewContent : (fetcher.data?.entry ?? null);

  return (
    // The sentinel wrapper always renders (zero-height while empty) — it is
    // the IntersectionObserver target. px inset matches the Top Banner /
    // breadcrumb container inset so the two banners share edges.
    <div ref={sentinelRef} data-testid="plp-bottom-banner" className="w-full">
      {content && (
        <div className="container mx-auto w-full px-2 pb-6 sm:px-6">
          {/* Section-level Suspense so a suspending Builder custom component
              is contained here and cannot bubble past the route <Outlet> into
              a page-level skeleton (same rationale as the Top Banner slot). */}
          <Suspense fallback={null}>
            <PrefetchedBuilderContent
              model={ModelMap.plpBottomBanner}
              content={content}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}
